import { useMemo } from "react";
import { useQuery } from "react-query";
import _ from "lodash";

import { getGraphsWhatsapp } from "../../../services/metricsWhatsapp";
import { translateStatusSends } from "../../../utils/format";

import { Header } from "../Header";
import { CardGraph } from "../CardGraph";
import { CardDashboard } from "../CardDashboard";

import { Box, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import { useStyles } from "../styles";

export function Whatsapp() {
  const styles = useStyles();

  const responseGraphsWhatsapp = useQuery(
    "getGraphsWhatsapp",
    getGraphsWhatsapp
  );

  const sentWithConfirmation = useMemo(() => {
    return responseGraphsWhatsapp.data
      ?.filter((_resp) => _resp.status === "ENVIADO COM CONFIRMACAO")
      ?.map((_resp) => _resp.total)
      ?.reduce((previous, current) => previous + current, 0);
  }, [responseGraphsWhatsapp]);

  const totalMessages = useMemo(() => {
    return responseGraphsWhatsapp.data
      ?.map((_resp) => _resp.total)
      ?.reduce((previous, current) => previous + current, 0);
  }, [responseGraphsWhatsapp]);

  const sentWithoutConfirmation = useMemo(() => {
    return responseGraphsWhatsapp.data
      ?.filter((_resp) => _resp.status === "ENVIADO SEM CONFIRMACAO")
      ?.map((_resp) => _resp.total)
      ?.reduce((previous, current) => previous + current, 0);
  }, [responseGraphsWhatsapp]);

  const openMessages = useMemo(() => {
    return responseGraphsWhatsapp.data
      ?.filter((_resp) => _resp.status === "MENSAGEM ABERTA")
      ?.map((_resp) => _resp.total)
      ?.reduce((previous, current) => previous + current, 0);
  }, [responseGraphsWhatsapp]);

  let response = useMemo(() => {
    const rep = responseGraphsWhatsapp?.data?.map((_cont) => ({
      label: _cont.date,
      value: _cont.total,
      status: translateStatusSends(_cont.status),
    }));

    return rep;
  }, [responseGraphsWhatsapp]);

  const graphsWhatsapp = useMemo(() => {
    if (!response || response.length === 0) {
      return {
        delived_unread: [],
        sent: [],
        received: [],
        canceled: [],
        error: [],
        read: [],
      };
    }

    // response = _.groupBy(response, _resp => _resp.status);
    const result = _.groupBy(response, (_resp) => _resp.status);

    return result;
  }, [response]);

  const cardNumbers = useMemo(() => {
    return {
      received: sentWithConfirmation ?? 0,
      total: totalMessages ?? 0,
      sent: sentWithoutConfirmation ?? 0,
      read: openMessages ?? 0,
    };
  }, [
    totalMessages,
    sentWithoutConfirmation,
    openMessages,
    sentWithConfirmation,
  ]);

  const { delived_unread, sent, received, read, error } = graphsWhatsapp;

  const dataWpUnread = useMemo(() => {
    return delived_unread?.map((value) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [delived_unread]);

  const dataWpSent = useMemo(() => {
    return sent?.map((value) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [sent]);

  const dataWpReceived = useMemo(() => {
    return received?.map((value) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [received]);

  const dataWpRead = useMemo(() => {
    return read?.map((value) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [read]);

  const dataError = useMemo(() => {
    return error?.map((value) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [error]);

  if (responseGraphsWhatsapp.isLoading) {
    return (
      <Stack
        spacing={2}
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="62vh"
      >
        <Typography variant="subtitle1">Buscando informações</Typography>
        <LinearProgress variant="indeterminate" sx={{ width: "50%" }} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} overflow="overlay" height="62vh">
      <Box className={styles.root}>
        <Header title="Whatsapp" />
        <Stack>
          <Grid
            container
            display="flex"
            p="24px 24px 0 0"
            spacing={2}
            flexWrap="wrap"
          >
            <Grid item md={5.5} lg={3} width="100%">
              <CardDashboard
                insights={cardNumbers.received}
                title={"Enviado com confirmação"}
                text="A quantidade de mensagens que foram enviadas com confirmação."
              />
            </Grid>
            <Grid item md={5.5} lg={3} width="100%">
              <CardDashboard
                insights={cardNumbers.total}
                title={"Total de mensagens enviadas"}
                text={
                  "A soma de mensagens que você enviou em todas as publicações."
                }
              />
            </Grid>
            <Grid item md={5.5} lg={3} width="100%">
              <CardDashboard
                insights={cardNumbers.sent}
                title={"Enviado sem confirmação"}
                text={
                  "A quantidade de contatos que não visualizaram suas mensagens."
                }
              />
            </Grid>
            <Grid item md={5.5} lg={3} width="100%">
              <CardDashboard
                insights={cardNumbers.read}
                title={"Mensagens que foram lidas"}
                text={
                  "A quantidade de contatos que vizualizaram suas mensangens."
                }
              />
            </Grid>
          </Grid>
          <Stack flexDirection="column" p="24px 0" gap={3}>
            <Header />
            <Grid
              container
              display="flex"
              p="24px 24px 0 0"
              spacing={4}
              flexWrap="wrap"
            >
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Enviadas"
                  text="Quantas mensagens foram enviadas"
                  data_type="SENT"
                  data={[{
                    name: 'Enviadas',
                    data: dataWpSent
                  }]}
                />
              </Grid>
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Recebidos"
                  text="Quantos números receberam as mensagens"
                  data_type="RECEIVED"
                  data={[{
                    name: 'Recebidos',
                    data: dataWpReceived
                  }]}
                />
              </Grid>
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Lidos"
                  text="Quantos números leram as mensagens"
                  data_type="READ"
                  data={[{
                    name: 'Lidos',
                    data: dataWpRead
                  }]}
                />
              </Grid>
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Não lidos"
                  text="Quantos números receberam as mensagens mas não leram"
                  data_type="UNREAD"
                  data={[{
                    name: 'Não lidos',
                    data: dataWpUnread
                  }]}
                />
              </Grid>
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Erros"
                  text="Quantas mensagens deram erro"
                  data_type="ERROR"
                  data={[{
                    name: 'Erros',
                    data: dataError
                  }]}
                />
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
